import React, { Fragment, useContext, useEffect, useMemo, useState } from 'react';
import { Form, FormSpy } from 'react-final-form';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createDecorator from 'final-form-calculate';
import arrayMutators from 'final-form-arrays';
import FormDialog from './FormDialog';
import CardCustomisationTabs from 'components/CardCustomisationTabs';
import { CGProduct } from 'types/shopify';
import debug from 'debug';
import { getCardValues, getCardVariantIdByMediumAndValue } from 'lib/product';
import Button from '@mui/material/Button';
import DialogContent from '@mui/material/DialogContent';
import Container from '../Layout/Container';
import DebugContainer from 'components/Debug/DebugContainer';
import useNotification from 'effects/useNotification';
import { shopifyContext } from 'contexts/ShopifyContext';
import pluralize from 'pluralize';
import { availableSteps, availableComponents, hideContinueButton, whenToDisableSteps, getInitialDisableSteps } from './configurerFlags';
import { CGCardConfigFormValues, CGCardConfigLineItem, CGCardConfigRecipient } from 'types/cardForm';
import { FormApi } from 'final-form';
import { RECIPIENT_COUNT_MULTIPLE, RECIPIENT_TYPE_SELF, RECIPIENT_TYPE_SOMEONE_ELSE } from 'utilities/constants';
import { v4 } from 'uuid';
import { collectionsContext } from 'contexts/CollectionsContext';
import { cgtag } from 'utilities/gtag';
import { serverErrorMessage } from 'utilities/constants';
import { shopifyExtractId } from 'lib/shopifyHelpers';

const d = debug('cardConfigurerForm');
let renderCount = 0;

export interface CardConfigurerStepProps {
  index: number;
  step: number;
  values: CGCardConfigFormValues;
  product: CGProduct;
  form: FormApi<CGCardConfigFormValues>;
  handleSubmit: any;
  onChange?: (step: number) => any;
  steps?: string[];
  initialValues: CGCardConfigFormValues;
  submitButton: React.ReactNode;
}

interface CardConfigurerFormProps {
  initialValues?: Partial<CGCardConfigFormValues>;
  initialStep?: number;
  product: CGProduct;
  onClose: () => void;
  onSuccess: () => void;
}

const useStyles = makeStyles(
  (theme: Theme) => ({
    root: {},
    form: {
      flexGrow: 1,
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      overflowX: 'hidden',
    },
    footer: {
      position: 'fixed',
      bottom: 0,
    },
    footerToolbar: {
      maxHeight: 60,
    },
    submitButton: {
      minWidth: 'min( 400px, 100%)',
      '&.Mui-disabled': {
        background: 'none',
        color: theme.palette.action.disabled,
      },
    },
    dialogContent: {
      padding: 0,
    },
    dialogActions: {
      justifyContent: 'center',
      padding: theme.spacing(3),
      [theme.breakpoints.down('md')]: {
        padding: theme.spacing(2),
      },
    },
    configPanels: {
      flexGrow: 1,
      overflowY: 'auto',
    },
    tabBarWrapper: {
      [theme.breakpoints.up('md')]: {
        height: `calc(2rem + ${theme.spacing(2)})`,
      },
    },
    tabBarFixedWrapper: {
      [theme.breakpoints.up('md')]: {
        backgroundColor: 'black',
        position: 'fixed',
        left: theme.spacing(3),
        right: theme.spacing(3),
        zIndex: 10,
      },
    },
  }),
  { name: 'CardConfigurerForm' },
);

const CardConfigurerForm: React.FC<CardConfigurerFormProps> = ({ product, onClose, onSuccess, initialValues = {}, initialStep = 0 }) => {
  const classes = useStyles();
  const showMessage = useNotification();
  const { addLineItems } = useContext(shopifyContext);
  const { loadProductByHandle } = useContext(collectionsContext);
  const [step, setStep] = useState(initialStep);
  const [disabledSteps, setDisabledSteps] = useState<string[]>(getInitialDisableSteps);
  const onValidate = async (values: CGCardConfigFormValues) => {};
  const steps = availableSteps(product.productType);
  const stepCount = steps.length - 1;
  const components = availableComponents(product.productType);

  const handleClose = () => {
    onClose();
  };

  const addDisabledStep = (disabledStep: string) => {
    if (!disabledSteps.includes(disabledStep)) {
      setDisabledSteps((prevSteps) => [...prevSteps, disabledStep]);
    }
  };
  const removeDisabledStep = (disabledStep: string) => {
    if (disabledSteps.includes(disabledStep)) {
      setDisabledSteps((prevSteps) => prevSteps.filter((step) => step !== disabledStep));
    }
  };

  const updateDisableStep = (step: string, shouldDisable: boolean) => {
    if (shouldDisable) {
      addDisabledStep(step);
    } else {
      removeDisabledStep(step);
    }
  };

  const nextStep: (current: number) => number = (current) => {
    let next = current;
    do {
      next++;
    } while (next < steps.length && disabledSteps.includes(steps[next]));

    return next;
  };

  const onSubmit = async (values: CGCardConfigFormValues, form: FormApi) => {
    try {
      d('submit', values);
      if (step <= stepCount - 1 && nextStep(step) <= stepCount) {
        setStep(nextStep);
      } else {
        d('!!!finalSubmit', values);

        if (values.cardDesignHandle && !values.cardDesignVariantId) {
          showMessage(`Error adding: Cannot find variant ID for ${values.cardDesignHandle}`, 'error');
          console.error(`Error adding: Cannot find variant ID for ${values.cardDesignHandle}. Medium:"${values.cardMedium}". Value:"${values.cardValue}"`);
          return;
        }

        const finalVariantId = values.cardDesignVariantId || values.variantId;
        if (finalVariantId) {
          // Add to the cart...

          // Prepare card.gift custom attributes....
          const cgData: CGCardConfigLineItem = {
            recipients: values.recipients
              ? values.recipients.map((recipient) => {
                  const formattedRecipient: CGCardConfigRecipient = {
                    ref: v4(),
                  };
                  if (recipient.name) formattedRecipient.name = recipient.name;
                  if (recipient.email) formattedRecipient.email = recipient.email;
                  if (recipient.phone) formattedRecipient.phone = recipient.phone;
                  if (recipient.message || values.recipientMessage) formattedRecipient.message = recipient.message || values.recipientMessage;
                  return formattedRecipient;
                })
              : [{ ref: v4(), type: RECIPIENT_TYPE_SELF, message: values.recipientMessage }],
          };

          // Provide the delivery time...
          if (values.deliveryType && values.deliveryType === 'scheduled' && values.deliveryTime) cgData.deliveryTime = values.deliveryTime;

          // Custom video reference...
          if (values.recipientVideo && values.recipientVideo.key) {
            cgData.customVideo = values.recipientVideo.key;
          }

          // Custom image reference...
          if (values.cardCustomImage && values.cardCustomImage.key) {
            cgData.customImage = values.cardCustomImage.key;
            // cgData.customImageTempUrl = values.cardCustomImage.url;
          }

          // Configure card attributes
          const cardAttributes = [{ key: '_card.gift', value: JSON.stringify(cgData) }];

          // Configure card item display attributes
          if (values.recipientCount === RECIPIENT_COUNT_MULTIPLE && cgData.recipients) {
            cardAttributes.push({ key: 'For', value: pluralize('Recipient', cgData.recipients.length, true) });
          } else {
            if (values.recipients && values.recipients.length > 1) throw new Error('Too many recipients...');
            if (values.recipientType === RECIPIENT_TYPE_SOMEONE_ELSE) {
              if (!values.recipients || values.recipients.length === 0) throw new Error('No recipient!?');
              const recipient = values.recipients[0];
              if (recipient.name) cardAttributes.push({ key: 'For', value: recipient.name });
              if (recipient.email) cardAttributes.push({ key: 'Email', value: recipient.email });
              if (recipient.phone) cardAttributes.push({ key: 'Phone', value: recipient.phone });
              if (recipient.message) cardAttributes.push({ key: 'Message', value: recipient.message });
            }
          }
          const finalQuantity = cgData.recipients ? cgData.recipients.length : 1;
          await addLineItems([
            {
              variantId: finalVariantId,
              quantity: finalQuantity,
              customAttributes: cardAttributes,
            },
          ])
            .then((x) => {
              if (x.errors === undefined) {
                showMessage('Added', 'success');
                onSuccess();
                return true;
              } else {
                d(x);
                showMessage('Error adding item...', 'error');
              }
            })
            .catch((err) => {
              showMessage('Error adding: ' + err.message, 'error');
            });
        } else {
          showMessage('No variant ID?', 'error');
        }
      }
    } catch (err: any) {
      showMessage(serverErrorMessage, 'error');
    }
  };

  const formDecorator = useMemo(
    () =>
      createDecorator(
        // Create a decorator to handle cardMedium changes...
        {
          field: 'cardMedium', // when medium changes...
          updates: {
            variantId: (cardMedium, allValues: any): string => {
              const cardValue = allValues.cardValue;
              return getCardVariantIdByMediumAndValue(product, cardMedium, cardValue) || (undefined as any);
            },
            cardValue: (cardMedium, allValues: any) => {
              const currentValue = allValues.cardValue;
              const availableValues = getCardValues(product, cardMedium).map((option) => option.value);
              d('adjustCardValue', cardMedium, currentValue, availableValues);
              if (!availableValues.length) {
                console.warn('card without values', availableValues);
                return null; // TODO - check this one...
              }
              if (availableValues.indexOf(currentValue) !== -1) {
                // Current value is fine!
                return currentValue;
              } else {
                // Select a default value (the first value)
                return availableValues[0];
              }
            },
          },
        },
        // This decorator automatically stores the appropriate variantId in the form...
        {
          field: 'cardValue', // when medium changes...
          updates: {
            variantId: (cardValue, allValues: any): string => {
              const cardMedium = allValues.cardMedium;
              return getCardVariantIdByMediumAndValue(product, cardMedium, cardValue) || (undefined as any);
            },
            cardDesignVariantId: async (cardValue, allValues: any) => {
              if (
                // (allValues.cardDesign === 'predesign' || allValues.cardDesign === 'custom') &&
                allValues.cardDesignHandle
              ) {
                const designProduct = await loadProductByHandle(allValues.cardDesignHandle);
                d('updating cardDesignVariantId');
                if (designProduct) {
                  d('loadedCustomDesign', designProduct);
                  const nextVariantID = getCardVariantIdByMediumAndValue(designProduct, allValues.cardMedium, cardValue);
                  d('calculatedVariantId', nextVariantID);
                  if (nextVariantID) return nextVariantID;
                  return null;
                } else {
                  return null;
                }
              } else {
                return undefined;
              }
            },
          },
        },
        {
          // This decorator keeps track of the variantId for the custom card design...
          field: 'cardDesignHandle',
          updates: {
            cardDesignVariantId: async (cardDesignHandle, allValues: any) => {
              if (
                // (allValues.cardDesign === 'predesign' || allValues.cardDesign === 'custom') &&
                cardDesignHandle
              ) {
                const designProduct = await loadProductByHandle(cardDesignHandle);
                if (designProduct) {
                  const nextVariantID = getCardVariantIdByMediumAndValue(designProduct, allValues.cardMedium, allValues.cardValue);
                  if (nextVariantID) return nextVariantID;
                  return null;
                } else {
                  return null;
                }
              } else {
                return undefined;
              }
            },
          },
        },
      ),
    [product, loadProductByHandle],
  );

  // GTM Monitoring of steps...
  useEffect(() => {
    cgtag('card_configurer_view', {
      step_index: step,
      step_name: steps[step],
      item_id: shopifyExtractId(product.id.toString()),
      item_name: product.title,
    });
    // TODO - Stop the configurer pages re-rendering
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);
  d('renderForm', renderCount++, 'step', step, steps[nextStep(step)]);

  return (
    <Form
      initialValues={initialValues}
      validate={onValidate}
      onSubmit={onSubmit}
      decorators={[formDecorator]}
      mutators={{
        ...arrayMutators,
      }}
    >
      {({ handleSubmit, form, submitting, values, initialValues, valid, pristine }) => {
        return (
          <FormDialog onClose={handleClose}>
            <DialogContent className={classes.dialogContent}>
              <Container sx={{ minHeight: '100%', display: 'flex' }}>
                <form onSubmit={handleSubmit} className={classes.form}>
                  <div className={classes.tabBarWrapper}>
                    <div className={classes.tabBarFixedWrapper}>
                      <CardCustomisationTabs currentTab={step} disabledSteps={disabledSteps} onChange={setStep} product={product} />
                    </div>
                  </div>
                  <div className={classes.configPanels}>
                    {components.map((Item, index) => {
                      return (
                        <Item
                          key={'card-step-' + index}
                          index={index}
                          values={values}
                          form={form}
                          step={step}
                          product={product}
                          handleSubmit={handleSubmit}
                          onChange={setStep}
                          steps={steps}
                          initialValues={initialValues}
                          submitButton={
                            <Fragment>
                              {!hideContinueButton(step, product, values) && (
                                <Button
                                  // Continue / Submit button
                                  onClick={handleSubmit}
                                  className={classes.submitButton}
                                  disabled={
                                    // pristine ||
                                    submitting || !valid
                                  }
                                  type="submit"
                                  variant="outlined"
                                  size="large"
                                  color="secondary"
                                >
                                  {step === stepCount || nextStep(step) > stepCount ? 'Add To Cart' : `Continue to ${steps[nextStep(step)]}`}
                                </Button>
                              )}
                            </Fragment>
                          }
                        />
                      );
                    })}
                  </div>
                  <DebugContainer data={{ values }} />
                </form>
              </Container>
            </DialogContent>
            <FormSpy
              subscription={{ values: true, dirty: true }}
              onChange={({ values, dirty }) => {
                if (dirty) {
                  whenToDisableSteps(updateDisableStep, values, product);
                }
              }}
            />
          </FormDialog>
        );
      }}
    </Form>
  );
};

export default CardConfigurerForm;
